import {
  ClaimProcessingStatus,
  IEdiConversionFile,
  IEdiSplitBatch,
  IEdiSplitPiece,
} from "../../../types/ediConversionFile";

// How often the EDI Files tabs refresh while something is in flight. 5 s keeps the UI current during
// a multi-stage parse while staying well under the API's per-user rate limit.
export const EDI_POLL_INTERVAL_MS = 5000;

const isWorking = (status?: ClaimProcessingStatus | null) =>
  status === "pending" || status === "processing";

// CLE-2921 — a piece created by "Split 835 Files" that waits for someone to press "Process now".
export const isHeld = (file: { uploadStatus?: string | null }): boolean =>
  file.uploadStatus === "held";

// CLE-2940 — a file over 10,000 claims gets no flat CSV at parse time; its CSV button serves the
// aggregated CSV instead.
export const isFlatCsvSkipped = (file: IEdiConversionFile): boolean =>
  file.metadata?.flatCsvSkipped === true;

/**
 * Is this conversion file still moving through upload → parse → claim processing? Drives the
 * 835 Conversion Files list polling. uploadStatus counts too — async parsing can run for tens of
 * seconds before claimProcessingStatus even leaves 'pending'.
 *
 * A held file is never in flight: it has never been parsed and has no job, so its four stages sit
 * at their 'pending' defaults until someone processes it. Counting it would poll forever.
 */
export const isEdiFileInFlight = (file: IEdiConversionFile): boolean => {
  if (isHeld(file)) return false;
  return (
    file.uploadStatus === "uploading" ||
    file.uploadStatus === "uploaded" ||
    file.uploadStatus === "parsing" ||
    isWorking(file.claimProcessingStatus) ||
    isWorking(file.cptCoOccurrenceStatus) ||
    isWorking(file.implantAssociationStatus) ||
    isWorking(file.cptCodeQuantityStatus)
  );
};

/**
 * Can this file be split? It has to be fully parsed and processed (the API answers 409 otherwise)
 * and carry a provider (400 otherwise). Legacy rows without an uploadStatus count as parsed.
 */
export const canSplitEdiFile = (file: IEdiConversionFile): boolean =>
  (file.uploadStatus ?? "parsed") === "parsed" &&
  file.claimProcessingStatus === "completed" &&
  !!file.providerId;

// A piece is in flight once processing has started and until claim processing settles. A held
// piece (stages at their 'pending' defaults) and a piece whose upload or parse failed are not.
export const isSplitPieceInFlight = (piece: IEdiSplitPiece): boolean => {
  if (
    piece.uploadStatus === "uploading" ||
    piece.uploadStatus === "uploaded" ||
    piece.uploadStatus === "parsing"
  ) {
    return true;
  }
  return (
    piece.uploadStatus === "parsed" && isWorking(piece.claimProcessingStatus)
  );
};

export const isSplitBatchActive = (batch: IEdiSplitBatch): boolean =>
  batch.status === "queued" || batch.status === "running";

// The "Split 835 Files" list polls while any batch is splitting, waiting for a retry of a failed
// attempt, or has a piece processing.
export const isSplitBatchInFlight = (batch: IEdiSplitBatch): boolean =>
  isSplitBatchActive(batch) ||
  batch.retryPending ||
  batch.pieces.some(isSplitPieceInFlight);
